'use client'

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { searchExact } from '../lib/search-service'
import { useOffline } from '@/components/offline-context'

export function useQuickSearch() {
  const [activeText, setActiveText] = useState<string>('')
  const [activeScriptType, setActiveScriptType] = useState<
    'simplified' | 'traditional' | undefined
  >()
  const { isOfflineEnabled, isReady, isQueryEnabled } = useOffline()
  const isOffline = isOfflineEnabled && isReady

  const { data: results, isLoading } = useQuery({
    // Remove isOffline from query key to prevent cache invalidation
    // The queryFn will handle offline/online mode internally
    queryKey: ['searchExact', activeText, activeScriptType],
    queryFn: async () => {
      if (!activeText.trim() || !activeScriptType) return []
      return await searchExact(activeText.trim(), activeScriptType, isOffline)
    },
    enabled: isQueryEnabled(activeText.length > 0 && !!activeScriptType),
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes - dictionary data is static
    gcTime: 1000 * 60 * 60, // Keep in cache for 1 hour
    refetchOnMount: false, // Don't refetch when component mounts if data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: false // Don't refetch on reconnect
  })

  const search = (text: string, scriptType?: 'simplified' | 'traditional') => {
    if (text.trim() && scriptType) {
      setActiveText(text.trim())
      setActiveScriptType(scriptType)
    } else {
      setActiveText('')
      setActiveScriptType(undefined)
    }
  }

  return {
    results,
    isLoading,
    search,
    clearSearch: () => search('', undefined)
  }
}
